/** React hooks over the transport. Components never touch the socket directly. */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { getServerState, getState, subscribeState, type TransportState } from './store.ts';
import { request } from './socket.ts';
import type { MethodName } from '../../shared/protocol/methods.ts';
import type { ProtocolError } from '../../shared/protocol/errors.ts';

export function useConnection(): TransportState {
  return useSyncExternalStore(subscribeState, getState, getServerState);
}

export interface QueryResult<T> {
  data: T | null;
  error: ProtocolError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Runs an RPC when the socket is ready and re-runs it on reconnect. Deliberately
 * simple: the live parts of this app come from subscriptions, not polling.
 */
export function useQuery<T>(method: MethodName, payload?: unknown): QueryResult<T> {
  const { status } = useConnection();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ProtocolError | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (status !== 'ready') return;
    let cancelled = false;
    setLoading(true);
    request<T>(method, payload)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err: ProtocolError) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // payload is compared by value through JSON: these payloads are tiny.
  }, [method, JSON.stringify(payload ?? null), status, nonce]);

  return { data, error, loading, reload };
}
